import React, { useEffect, useMemo, useState } from 'react';

import {
  ScreenAllFilters,
  ScreenFilters,
  ScreenFiltersApply,
} from 'components/Filters';

import { CollapsibleBlock, Divider } from '@gogame/components';
import { View, Stack, Grid, Paper } from '@gogame/components';
import { useChildComponent } from '@gogame/hooks-ts';
import {
  Navigate,
  Route,
  withScreen,
  useParams,
  useScreenTitle,
  useScreenGo,
  Screen,
} from '@gogame/screens';
import { Sentry } from '@gogame/components';
import ConceptFamiliesActions from 'scenes/Gallery/GalleryPage/ConceptFamilies/Actions';
import {
  ConceptFamilyDetailsGeneral,
  ConceptFamilyDetailsInfo,
} from 'scenes/Gallery/GalleryPage/ConceptFamilies/ConceptFamilyDetails';
import { useConceptFamilyDetails } from 'scenes/Gallery/GalleryPage/ConceptFamilies/data';
import {
  ConceptsDataTable,
  ConceptsDataTableProvider,
} from 'scenes/Gallery/GalleryPage/Concepts/ConceptsDataTable';
import {
  CreativesDataTable,
  CreativesDataTableProvider,
} from 'scenes/Gallery/GalleryPage/Creatives/CreativesDataTable';
import AllFilters from 'scenes/Gallery/GalleryPage/Filters/AllFilters';
import PrimaryFilters from 'scenes/Gallery/GalleryPage/Filters/PrimaryFilters';
import {
  TAB_CONCEPTS,
  TAB_CONCEPT_FAMILIES,
  TAB_CREATIVES,
  TAB_DETAILS,
  TAB_ATTACHMENTS,
} from 'scenes/Gallery/GalleryPage/constants';
import { EMPTY_FILTERS } from 'scenes/Gallery/GalleryPage/schemas';
import screenReducer from 'scenes/Gallery/GalleryPage/screenReducer';
import { useGalleryPermissions } from 'scenes/Gallery/GalleryPage/hooks';

import DetailsFile from './components/DetailsFile';
import GridTabs from './components/GridTabs';
import AttachmentsTab from './components/AttachmentsTab';
import Tabs from './components/Tabs';

const ConceptFamiliesPreviewScreen = withScreen({
  name: 'gallery-v4-concept-families-preview',
  reducer: screenReducer,
})(() => {
  const params = useParams();
  const [, setTitle] = useScreenTitle();

  const go = useScreenGo();
  const [childComponent, openChildComponent] = useChildComponent();

  const { isAllowAttachments } = useGalleryPermissions();

  const conceptFamilyId = useMemo(() => {
    if (params.conceptFamilyId) {
      return Number(params.conceptFamilyId);
    }
  }, [params.conceptFamilyId]);

  const {
    data: conceptFamily,
    isLoading,
    isRefetching,
  } = useConceptFamilyDetails({
    id: conceptFamilyId,
  });

  useEffect(() => {
    if (!conceptFamily) {
      return;
    }
    setTitle(`Concept Family «${conceptFamily?.name}»`);
  }, [conceptFamily]);

  const handleDelete = async () => {
    go(`../${TAB_CONCEPT_FAMILIES}`);
  };
  const { '*': tab } = useParams();

  const handleTabChange = (_e, newActiveTabId) => {
    go(`./${newActiveTabId}`);
  };

  const tabs = <GridTabs value={tab} onChange={handleTabChange} />;

  const [activeTab, setActiveTab] = useState(TAB_DETAILS);

  return (
    <React.Fragment>
      <Screen.Toolbar />
      <Screen.Body>
        <ScreenFilters emptyValues={EMPTY_FILTERS}>
          <PrimaryFilters />
          <ScreenAllFilters sidePage={AllFilters} />
          <ScreenFiltersApply />
        </ScreenFilters>

        <Stack spacing={2} flex={'1 0 auto'}>
          <CollapsibleBlock
            showLabel={'Show concept families details'}
            hideLabel={'Hide concept families details'}
          >
            <Stack>
              <Paper variant={'outlined'}>
                <View loading={isLoading} fetching={isRefetching}>
                  <Stack
                    direction={'row'}
                    alignItems={'center'}
                    justifyContent={'space-between'}
                    spacing={3}
                    pt={2}
                    pl={2}
                    pr={2}
                    pb={1}
                  >
                    {conceptFamily ? (
                      <React.Fragment>
                        <DetailsFile data={conceptFamily} />
                        <ConceptFamiliesActions
                          actions={conceptFamily?.actions}
                          data={conceptFamily}
                          onRemove={handleDelete}
                          onOpenChildComponent={openChildComponent}
                        />
                      </React.Fragment>
                    ) : null}
                  </Stack>
                  <Tabs
                    conceptFamilyId={conceptFamilyId}
                    activeTab={activeTab}
                    onChange={setActiveTab}
                  />
                  <Divider />
                  {
                    {
                      [TAB_DETAILS]: conceptFamily ? (
                        <Grid container spacing={3} p={2}>
                          <Grid item xs={6}>
                            <ConceptFamilyDetailsGeneral
                              data={conceptFamily}
                              withNameField={false}
                              openChildComponent={openChildComponent}
                              termWidth={'140px'}
                            />
                          </Grid>
                          <Grid item xs={6}>
                            <ConceptFamilyDetailsInfo
                              data={conceptFamily}
                              openChildComponent={openChildComponent}
                              termWidth={'140px'}
                            />
                          </Grid>
                        </Grid>
                      ) : null,
                      [TAB_ATTACHMENTS]: isAllowAttachments ? (
                        <Stack>
                          <AttachmentsTab conceptFamilyId={conceptFamilyId} />
                        </Stack>
                      ) : null,
                    }[activeTab]
                  }
                </View>
              </Paper>
            </Stack>
          </CollapsibleBlock>

          <Stack flex={'1 0 auto'}>
            <ConceptsDataTableProvider>
              <CreativesDataTableProvider>
                <Sentry.Routes>
                  <Route
                    path={`${TAB_CONCEPTS}/*`}
                    element={<ConceptsDataTable height={700} tabs={tabs} />}
                  />
                  <Route
                    path={`${TAB_CREATIVES}/*`}
                    element={<CreativesDataTable height={700} tabs={tabs} />}
                  />
                  <Route
                    path={'*'}
                    element={<Navigate to={`${TAB_CONCEPTS}`} replace={true} />}
                  />
                </Sentry.Routes>
              </CreativesDataTableProvider>
            </ConceptsDataTableProvider>
          </Stack>
        </Stack>
      </Screen.Body>
      {childComponent}
    </React.Fragment>
  );
});

export default ConceptFamiliesPreviewScreen;
